/**
 * Cleanup pass over a single dictated utterance (POST /dictation/cleanup).
 *
 * Streaming transcription sees one utterance at a time, so it cannot know
 * whether a fragment continues the previous sentence or starts a new one. The
 * server gets the utterance plus the text before it and returns the corrected
 * form.
 *
 * Advisory by design: this runs while the physician is still dictating and the
 * raw text is already on screen, so every failure resolves to the raw utterance
 * rather than throwing.
 */

import { createApiClient } from "~/api/apiClient";
import type { DictationSurface } from "~/services/webrtcTranscription";

/** Matches the server's `MAX_PRECEDING_CHARS`; only the tail is useful context. */
export const MAX_PRECEDING_CHARS = 2000;

export async function cleanDictationUtterance(
  utterance: string,
  precedingText: string,
  surface: DictationSurface = "assessment",
): Promise<string> {
  try {
    const client = createApiClient();
    const { data } = await client.POST("/dictation/cleanup", {
      body: {
        utterance,
        surface,
        preceding_text: precedingText.slice(-MAX_PRECEDING_CHARS),
      },
    });
    return data?.cleaned_text || utterance;
  } catch {
    // Reported centrally by the api client's middleware. A dictation in
    // progress is the wrong place to surface it.
    return utterance;
  }
}
